import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/auth';
const AuthContext = createContext(undefined);
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // Verificar autenticação ao carregar
    useEffect(() => {
        let isMounted = true;
        const initAuth = async () => {
            try {
                if (authService.isAuthenticated()) {
                    const storedUser = authService.getStoredUser();
                    if (storedUser && isMounted) {
                        // Usar dados locais primeiro
                        setUser(storedUser);
                        // Só verificar no servidor se há um token válido
                        const token = authService.getToken();
                        if (token && authService.isAuthenticated()) {
                            try {
                                const currentUser = await authService.getCurrentUser();
                                if (isMounted) {
                                    setUser(currentUser);
                                }
                            }
                            catch (serverError) {
                                console.warn('Erro ao validar usuário no servidor:', serverError);
                                // Se o token for inválido, fazer logout
                                if (isMounted) {
                                    authService.logout();
                                    setUser(null);
                                }
                            }
                        }
                    }
                }
            }
            catch (error) {
                console.error('Erro ao verificar autenticação:', error);
                // Se houver erro, limpar dados locais
                if (isMounted) {
                    authService.logout();
                }
            }
            finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        initAuth();
        return () => {
            isMounted = false;
        };
    }, []);
    const login = async (credentials) => {
        setIsLoading(true);
        try {
            const response = await authService.login(credentials);
            setUser(response.user);
        }
        finally {
            setIsLoading(false);
        }
    };
    const logout = async () => {
        setIsLoading(true);
        try {
            await authService.logout();
            setUser(null);
        }
        finally {
            setIsLoading(false);
        }
    };
    const updateUser = async (userData) => {
        const updatedUser = await authService.updateProfile(userData);
        setUser(updatedUser);
    };
    const hasPermission = (roles) => {
        return authService.hasPermission(roles);
    };
    const isAdmin = () => {
        return authService.isAdmin();
    };
    const isTecnico = () => {
        return authService.isTecnico();
    };
    const isVendedor = () => {
        return authService.isVendedor();
    };
    const value = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        updateUser,
        hasPermission,
        isAdmin,
        isTecnico,
        isVendedor
    };
    return (_jsx(AuthContext.Provider, { value: value, children: children }));
};
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth deve ser usado dentro de um AuthProvider');
    }
    return context;
};
// Hook para proteção de rotas
export const useRequireAuth = (requiredRoles) => {
    const { isAuthenticated, user, hasPermission, isLoading } = useAuth();
    useEffect(() => {
        // Aguardar carregamento inicial antes de redirecionar
        if (isLoading)
            return;
        if (!isAuthenticated) {
            window.location.href = '/login';
            return;
        }
        if (requiredRoles && !hasPermission(requiredRoles)) {
            window.location.href = '/unauthorized';
            return;
        }
    }, [isAuthenticated, user, requiredRoles, hasPermission, isLoading]);
    return { isAuthenticated, user };
};
